import {
  presetRange,
  shiftPreset,
  type DateRange,
  type LocalDate,
  type WeekStart,
} from '@tahi/core';
import { Chip } from '../../../ui';
import { copy } from '../copy';
import type { RangeChoice } from '../drill';
import { rangeLabel } from '../format';
import { ICON_BTN, INPUT } from '../styles';

const CHOICES: { value: RangeChoice; label: string }[] = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
  { value: 'custom', label: 'Custom' },
];

interface RangePickerProps {
  preset: RangeChoice;
  range: DateRange;
  today: LocalDate;
  weekStartsOn: WeekStart;
  onChange(preset: RangeChoice, range: DateRange): void;
}

export function RangePicker({ preset, range, today, weekStartsOn, onChange }: RangePickerProps) {
  return (
    <div className="flex flex-col gap-3">
      <div role="group" aria-label={copy.rangeLabel} className="flex gap-1.5">
        {CHOICES.map((c) => (
          <Chip
            key={c.value}
            selected={preset === c.value}
            aria-pressed={preset === c.value}
            className="flex-1 justify-center px-2!"
            onClick={() =>
              onChange(
                c.value,
                c.value === 'custom' ? range : presetRange(c.value, today, weekStartsOn),
              )
            }
          >
            {c.label}
          </Chip>
        ))}
      </div>
      {preset === 'custom' ? (
        <div className="flex gap-2">
          <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-muted">
            {copy.from}
            <input
              type="date"
              value={range.start}
              max={range.end}
              onChange={(e) =>
                e.target.value && onChange('custom', { start: e.target.value, end: range.end })
              }
              className={INPUT}
            />
          </label>
          <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs text-muted">
            {copy.to}
            <input
              type="date"
              value={range.end}
              min={range.start}
              onChange={(e) =>
                e.target.value && onChange('custom', { start: range.start, end: e.target.value })
              }
              className={INPUT}
            />
          </label>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            aria-label={copy.previousPeriod}
            onClick={() => onChange(preset, shiftPreset(preset, range, -1, weekStartsOn))}
            className={ICON_BTN}
          >
            ‹
          </button>
          <p className="font-medium text-fg">{rangeLabel(preset, range)}</p>
          <button
            type="button"
            aria-label={copy.nextPeriod}
            onClick={() => onChange(preset, shiftPreset(preset, range, 1, weekStartsOn))}
            className={ICON_BTN}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
